<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<!--
  Compact table view: a lightweight DataTable-based list view.
  Uses the same datatableColumnConfig as ContentTableView but with a
  compact layout. Supports custom column rendering via `component` in
  column config. Actions column is configurable via `inlineActions` prop.
-->

<!-- eslint-disable vue/no-root-v-if -->
<template>
  <PrimeDataTable
    v-if="currentNode"
    :key="currentNode.id"
    :value="files"
    row-hover
    :filters="filters"
    :global-filter-fields="['name']"
    removable-sort
    :dt="compactDt"
    v-model:selection="selectedNodesCopy"
    selection-mode="multiple"
    data-key="id"
    :pt="compactPt"
    :sort-field="sortField"
    :sort-order="sortOrder"
    @sort="onSort"
  >
    <PrimeColumn selection-mode="multiple" header-style="width: 3rem" />
    <!-- Dynamic columns from config -->
    <PrimeColumn
      v-for="(col, index) in visibleColumns"
      :key="index"
      :sortable="!!col.sortField"
      :sort-field="col.sortField"
      :style="col.key === 'name' ? 'min-width: 12rem;' : ''"
    >
      <template #header>
        <span class="column-label">{{ col.label }}</span>
        <PrimeButton
          v-if="col.filterType"
          type="button"
          :icon="isColumnFiltered(col) ? 'ri-filter-fill' : 'ri-filter-line'"
          size="small"
          severity="primary"
          text
          rounded
          class="filter-toggle-btn"
          v-tooltip="$t('filter.toggle')"
          @click.stop="openFilterPopover($event, col)"
        />
      </template>
      <template #body="{ data }">
        <!-- Custom component rendering -->
        <component
          v-if="col.component"
          :is="col.component"
          v-bind="
            col.componentProps ? col.componentProps(data) : { node: data }
          "
        />
        <!-- Name column with icon and click -->
        <span
          v-else-if="col.key === 'name'"
          v-tooltip.bottom="getValue(data, col.key, col)"
          class="name-cell cursor-pointer"
          :class="{ underline: isCurrentNodeDisplayed(data) }"
          @click="navigateTo(data)"
        >
          <template v-if="data.isFolder">
            <i class="ri-folder-2-line folder-icon" />
            <PrimeButton
              v-if="data.permissions?.isInheritanceEnabled === false"
              type="button"
              icon="ri-shield-user-line"
              severity="warn"
              text
              rounded
              size="small"
              :aria-label="$t('restrictedPermissions')"
              v-tooltip="$t('restrictedPermissions')"
              @click.stop="$emit('open-members', data)"
            />
          </template>
          <template v-else>
            <i v-if="data.isLocked" class="ri-lock-line lock-icon" />
            <PrimeButton
              v-else-if="data.permissions?.isInheritanceEnabled === false"
              type="button"
              icon="ri-shield-user-line"
              severity="warn"
              text
              rounded
              size="small"
              :aria-label="$t('restrictedPermissions')"
              v-tooltip="$t('restrictedPermissions')"
              @click.stop="$emit('open-members', data)"
            />
            <PristyIcon
              v-tooltip="loadIconLabel(data)"
              class="file-icon cursor-pointer"
              :svg-file-name="loadIcon(data)"
              @click.stop="clickIcon(data)"
            />
          </template>
          {{ getValue(data, col.key, col) }}
        </span>
        <!-- Standard column with tooltip -->
        <span
          v-else-if="col.hasTooltip"
          v-tooltip.bottom="getValue(data, col.key, col)"
          class="text-overflow"
          :class="{
            'cursor-pointer': !!col.action,
            'line-clamp': !!col.maxLines,
          }"
          :style="col.maxLines ? { '-webkit-line-clamp': col.maxLines } : {}"
          @click="applyAction(col.action, data)"
        >
          {{ applyFormat(getValue(data, col.key, col), col.format) }}
        </span>
        <!-- Standard column without tooltip -->
        <span
          v-else
          :class="{ 'cursor-pointer': !!col.action }"
          @click="applyAction(col.action, data)"
        >
          {{ applyFormat(getValue(data, col.key, col), col.format) }}
        </span>
      </template>
    </PrimeColumn>

    <!-- Actions column -->
    <PrimeColumn :header="$t('column.actions')" header-style="width: 1%">
      <template #body="{ data }">
        <div class="actions-cell">
          <template
            v-for="(action, idx) in resolvedInlineActions(data)"
            :key="idx"
          >
            <PrimeButton
              v-if="action.visible !== false"
              v-tooltip.left="action.tooltip"
              type="button"
              :icon="action.icon"
              size="small"
              :severity="action.severity"
              :class="action.class"
              :disabled="disableInlineActions"
              text
              rounded
              @click="action.handler($event, data)"
            />
          </template>
          <PrimeButton
            v-if="!hideMoreActions"
            v-tooltip.left="$t('moreActions')"
            type="button"
            icon="ri-more-2-line"
            size="small"
            severity="secondary"
            :disabled="disableInlineActions"
            text
            rounded
            @click="toggleFileActionMenu($event, data)"
          />
        </div>
      </template>
    </PrimeColumn>
    <!-- Settings column -->
    <PrimeColumn header-style="width: 3rem; text-align: center;">
      <template #header>
        <div class="settings-header">
          <PrimeButton
            v-if="hasActiveFilters"
            v-tooltip.left="$t('filter.clearAll')"
            type="button"
            icon="ri-filter-off-line"
            size="small"
            severity="danger"
            text
            rounded
            @click="clearAllFilters"
          />
          <ColumnTogglePanel
            :columns="datatableColumnConfig"
            :is-small-screen="isSmallScreen"
            @update:columns="$emit('update:datatableColumnConfig', $event)"
          />
        </div>
      </template>
    </PrimeColumn>
  </PrimeDataTable>

  <!-- Filter popover -->
  <PrimePopover ref="filterPopoverRef">
    <div v-if="activeFilterColumn" class="filter-popover">
      <div class="filter-popover-header">
        <span class="filter-popover-title">{{ activeFilterColumn.label }}</span>
        <PrimeButton
          v-if="isColumnFiltered(activeFilterColumn)"
          :label="$t('filter.clear')"
          size="small"
          text
          severity="danger"
          @click="clearColumnFilter"
        />
      </div>
      <!-- Text filter: operator + input -->
      <div
        v-if="activeFilterColumn.filterType === 'text'"
        class="filter-popover-text"
      >
        <PrimeSelect
          v-model="textFilterOperator"
          :options="textFilterOperators"
          option-label="label"
          option-value="value"
          size="small"
          class="filter-operator-select"
        />
        <PrimeInputText
          v-model="textFilterValue"
          :placeholder="$t('filter.search')"
          size="small"
          class="filter-text-input"
          @keyup.enter="applyTextFilter"
        />
        <PrimeButton
          :label="$t('filter.apply')"
          size="small"
          @click="applyTextFilter"
        />
      </div>
      <!-- Date filter: from / to -->
      <div
        v-else-if="activeFilterColumn.filterType === 'date'"
        class="filter-popover-date"
      >
        <label class="filter-date-label">{{ $t("filter.from") }}</label>
        <PrimeDatePicker
          v-model="dateFilterFrom"
          date-format="dd/mm/yy"
          show-icon
          size="small"
          class="filter-date-input"
        />
        <label class="filter-date-label">{{ $t("filter.to") }}</label>
        <PrimeDatePicker
          v-model="dateFilterTo"
          date-format="dd/mm/yy"
          show-icon
          size="small"
          class="filter-date-input"
        />
        <PrimeButton
          :label="$t('filter.apply')"
          size="small"
          @click="applyDateFilter"
        />
      </div>
      <!-- Select/Boolean filter: checkboxes -->
      <div v-else class="filter-popover-options">
        <label
          v-for="option in currentFilterOptions"
          :key="option.value"
          class="filter-option"
        >
          <PrimeCheckbox
            :model-value="isOptionSelected(option.value)"
            :binary="true"
            @update:model-value="toggleFilterOption(option.value)"
          />
          <span>{{ option.label }}</span>
        </label>
        <span
          v-if="currentFilterOptions.length === 0"
          class="filter-popover-empty"
        >
          {{ $t("filter.noOptions") }}
        </span>
      </div>
    </div>
  </PrimePopover>
</template>

<script>
import { ref, toRef, getCurrentInstance, inject } from "vue";
import { useColumnFilter } from "@/composables/useColumnFilter.js";
import PristyIcon from "../PristyIcon.vue";
import ColumnTogglePanel from "./ColumnTogglePanel.vue";
import translationService from "@/services/TranslationService.js";
import formatService from "@/services/FormatService.js";
import iconeService from "@/services/IconeService.js";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Popover from "primevue/popover";
import Checkbox from "primevue/checkbox";
import Select from "primevue/select";
import InputText from "primevue/inputtext";
import DatePicker from "primevue/datepicker";

export default {
  name: "ContentCompactTableView",
  components: {
    PristyIcon,
    ColumnTogglePanel,
    PrimeButton: Button,
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimePopover: Popover,
    PrimeCheckbox: Checkbox,
    PrimeSelect: Select,
    PrimeInputText: InputText,
    PrimeDatePicker: DatePicker,
  },
  directives: {
    tooltip: Tooltip,
  },
  inject: ["isCurrentNodeDisplayed", "getLibelleById"],
  setup(props, { emit }) {
    const filterPopoverRef = ref(null);
    const { proxy } = getCurrentInstance();
    const injectedFormatters = {
      getActeurDisplayName: inject("getActeurDisplayName", null),
    };
    const filter = useColumnFilter({
      activeFilters: toRef(props, "activeFilters"),
      filterOptions: toRef(props, "filterOptions"),
      t: proxy.$t.bind(proxy),
      filterPopoverRef,
      emit,
      formatLabel: (value, formatFnName) => {
        const injected = injectedFormatters[formatFnName];
        if (typeof injected === "function") {
          return injected(value);
        }
        const fn = proxy[formatFnName];
        return typeof fn === "function" ? fn(value) : value;
      },
    });
    return { ...filter, filterPopoverRef };
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: null,
    },
    currentNode: {
      type: Object,
      default: null,
    },
    selectedNodes: {
      type: Array,
      default: () => [],
    },
    isPreviewPanelVisible: {
      type: Boolean,
      default: false,
    },
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
    datatableColumnConfig: {
      type: Array,
      default: () => [],
    },
    sortField: {
      type: String,
      default: null,
    },
    sortOrder: {
      type: Number,
      default: null,
    },
    inlineActions: {
      type: Array,
      default: () => [],
    },
    hideMoreActions: {
      type: Boolean,
      default: false,
    },
    disableInlineActions: {
      type: Boolean,
      default: false,
    },
    activeFilters: {
      type: Object,
      default: () => ({}),
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    "update:selectedNodes",
    "navigate-to",
    "toggle-file-action-menu",
    "click-icon",
    "sort-change",
    "update:datatableColumnConfig",
    "filter-change",
    "clear-all-filters",
    "open-members",
  ],
  computed: {
    visibleColumns() {
      return this.datatableColumnConfig.filter((col) => col.visible);
    },
    allSelected() {
      return (
        this.files.length > 0 && this.selectedNodes.length === this.files.length
      );
    },
    selectedNodesCopy: {
      get() {
        return this.selectedNodes;
      },
      set(value) {
        this.$emit("update:selectedNodes", value);
      },
    },
    compactDt() {
      return {
        headerCellBorderColor: "transparent",
        headerCellBackground: "var(--p-content-background)",
        bodyCellBorderColor: "transparent",
        rowBackground: "transparent",
        rowStripedBackground: "transparent",
      };
    },
    compactPt() {
      const pt = {
        bodyRow: ({ context }) => ({
          style: context.selected
            ? "background: color-mix(in srgb, var(--p-primary-color) 22%, var(--p-content-background))"
            : undefined,
        }),
        headerCheckbox: {
          root: {
            title: this.allSelected
              ? this.$t("deselectAll")
              : this.$t("selectAll"),
          },
        },
        column: {
          headerCell: {
            style:
              "padding: 0.4rem 0.5rem; font-size: 0.875rem; font-weight: 600; color: var(--p-text-muted-color); text-align: left; border-bottom: 1px solid var(--p-content-border-color); background: var(--p-content-hover-background)",
          },
          bodyCell: {
            style: "padding: 0.3rem 0.5rem; vertical-align: middle",
          },
        },
        tableContainer: {
          style:
            "border: 1px solid var(--p-content-border-color); border-radius: 0.5rem; overflow: hidden",
        },
      };
      if (this.isPreviewPanelVisible) {
        pt.root = { style: "max-width: 100%; overflow-x: auto" };
      }
      return pt;
    },
  },
  methods: {
    resolvedInlineActions(node) {
      return this.inlineActions.map((action) => ({
        ...action,
        icon:
          typeof action.icon === "function" ? action.icon(node) : action.icon,
        tooltip:
          typeof action.tooltip === "function"
            ? action.tooltip(node)
            : action.tooltip,
        severity:
          typeof action.severity === "function"
            ? action.severity(node)
            : action.severity,
        visible:
          typeof action.visible === "function" ? action.visible(node) : true,
        class:
          typeof action.class === "function"
            ? action.class(node)
            : action.class,
      }));
    },
    onSort(event) {
      this.$emit("sort-change", {
        sortField: event.sortField,
        sortOrder: event.sortOrder,
      });
    },
    navigateTo(node) {
      this.$emit("navigate-to", node);
    },
    clickIcon(node) {
      this.$emit("click-icon", node);
    },
    loadIcon(item) {
      return iconeService.getIcon(item);
    },
    loadIconLabel(item) {
      return iconeService.getIconLabel(item);
    },
    applyAction(actionFnName, row) {
      if (!actionFnName || typeof this[actionFnName] !== "function") {
        return;
      }
      this[actionFnName](row);
    },
    applyFormat(value, formatFnName) {
      if (!formatFnName || typeof this[formatFnName] !== "function") {
        return value;
      }
      return this[formatFnName](value);
    },
    getValue(obj, path, col) {
      if (col?.valueGetter) {
        return col.valueGetter(obj);
      }
      return path
        .replace(/\[(\w+)\]/g, ".$1")
        .replace(/\["(.+?)"\]|\['(.+?)'\]/g, ".$1$2")
        .split(".")
        .filter(Boolean)
        .reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
    },
    convertBytesToSize(str) {
      return translationService.convertBytesToSize(str);
    },
    toLocaleDateString(datestr) {
      return formatService.toLocaleDateString(datestr);
    },
    formatDate(value) {
      return formatService.formatDate(value);
    },
    toBoolean(value) {
      return value ? this.$t("yes") : this.$t("no");
    },
    toggleFileActionMenu(event, node) {
      this.$emit("toggle-file-action-menu", event, node);
    },
  },
};
</script>

<style lang="scss" scoped>
.settings-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.filter-toggle-btn {
  width: 1.5rem !important;
  height: 1.5rem !important;
  padding: 0 !important;
}

.name-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
  font-size: 0.875rem;
  word-break: break-word;
  overflow-wrap: break-word;
}

.file-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.folder-icon {
  color: var(--p-primary-color);
  font-size: 1rem;
  flex-shrink: 0;
}

.lock-icon {
  color: var(--p-orange-500);
  flex-shrink: 0;
}

.text-overflow {
  display: inline-block;
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.line-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cursor-pointer {
  cursor: pointer;
}

.underline {
  text-decoration: underline;
  color: var(--p-primary-color);
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  white-space: nowrap;

  :deep(.p-button) {
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
  }
}

.filter-popover {
  min-width: 12rem;
  max-width: 20rem;
}

.filter-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.filter-popover-title {
  font-weight: 600;
  font-size: 0.875rem;
}

.filter-popover-options {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 15rem;
  overflow-y: auto;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
}

:deep(.p-datatable-column-header-content) {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.p-datatable-column-header-content .column-label) {
  order: 0;
}

:deep(.p-datatable-sort-icon) {
  order: 1;
  color: var(--p-primary-color);
  --p-datatable-sort-icon-color: var(--p-primary-color);
  --p-datatable-sort-icon-hover-color: var(--p-primary-color);
}

:deep(.p-datatable-column-header-content .filter-toggle-btn) {
  order: 2;
}

.filter-popover-empty {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}

.filter-popover-text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-operator-select {
  width: 100%;
}

.filter-text-input {
  width: 100%;
}

.filter-popover-date {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-date-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.filter-date-input {
  width: 100%;
}
</style>
